import React, { useRef, useState } from "react";

interface UploadResult {
  filename: string;
  mimetype: string;
  size: number;
  cleaned?: string;
  raw?: string;
  error?: string;
}

export default function Docs() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function getOrCreateUid(): string {
    try {
      const existing = window.localStorage.getItem("uid");
      if (existing) return existing;
      const generated = (typeof crypto !== "undefined" && "randomUUID" in crypto)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem("uid", generated);
      return generated;
    } catch {
      return "";
    }
  }

  function getBackendBase(): string | undefined {
    const raw = import.meta.env.VITE_BACKEND_URL as string | undefined;
    if (!raw) return undefined;
    return raw.endsWith("/") ? raw.slice(0, -1) : raw;
  }

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files", f));

    setLoading(true);
    setError(null);
    try {
      const backendBase = getBackendBase();
      const apiKey = import.meta.env.VITE_BACKEND_API_KEY as string | undefined;
      const url = backendBase ? `${backendBase}/api/upload` : "/api/upload";
      const headers: Record<string, string> = {};
      if (backendBase && apiKey) headers["x-api-key"] = apiKey;
      if (backendBase) headers["x-user-id"] = getOrCreateUid();
      const resp = await fetch(url, {
        method: "POST",
        body: form,
        headers,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Upload failed");
      setResults(data.results || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="max-w-4xl mx-auto p-6 space-y-8">
      <h1 className="text-3xl font-bold text-primary">Document Parser</h1>

      <section
        className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50"
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <p className="text-gray-600">Click or drag files here to upload PDF, DOCX, or XLSX documents.</p>
      </section>

      {loading && <p className="text-sm text-primary">Uploading & parsing…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {results.length > 0 && (
        <section className="space-y-6">
          {results.map((r, idx) => (
            <div key={idx} className="border rounded-md p-4 bg-white shadow-sm">
              <h2 className="font-semibold text-primary mb-2">{r.filename}</h2>
              {r.error ? (
                <p className="text-red-600 text-sm">{r.error}</p>
              ) : (
                <pre className="whitespace-pre-wrap max-h-64 overflow-y-auto text-sm text-gray-800">
                  {r.cleaned?.slice(0, 2000) || "(no text)"}
                  {r.cleaned && r.cleaned.length > 2000 && "…"}
                </pre>
              )}
            </div>
          ))}
        </section>
      )}
    </main>
  );
}

